import { differenceInDays, isValid } from 'date-fns'

import { FundingNeedResponse } from '@/models/FundingNeeds'
import { ScanResponse } from '@/models/Scans'
import { useLatestScanResultsByNeedId, useOrgScanUsage } from './scans'
import { usePausedOrg } from './usePausedOrg'
import { parseUTCDate } from '@/utils/dateUtils'
import {
  STALE_SCAN_DAYS,
  EDITED_SINCE_SCAN_MESSAGE,
  getLatestScan,
  getScanBlock,
  hasScanAmount,
} from '@/utils/staleScan'
import { hasFreeUserReachedScanLimit } from '@/utils/utils'

/**
 * Shared by FundingNeedStaleWarningIcon and FundingNeedMatchActions so both
 * agree on the same staleness/can-scan state for a funding need without
 * duplicating the computation — React Query dedupes the underlying queries,
 * so calling this from two sibling components costs no extra network trips.
 */
export function useFundingNeedStaleness(
  token: string,
  fundingNeed: FundingNeedResponse,
  scanIds?: number[],
) {
  const {
    canWrite,
    isPaused,
    isCancelled,
    orgUuid,
    maxTotalScans,
    isFreeTierOrg,
  } = usePausedOrg()
  const {
    data: orgScanUsage,
    isLoading: usageLoading,
    isError: usageError,
  } = useOrgScanUsage(token, orgUuid)
  // A pending or failed usage query must not read as "0 scans used" — see
  // hasFreeUserReachedScanLimit for why unknown usage counts as at cap.
  const totalScansUsed =
    usageLoading || usageError ? undefined : orgScanUsage?.total_scans_used
  // Whether a scan would succeed. Gates View Results, which stays reachable
  // exactly when a re-scan is not an option.
  const canScan =
    canWrite &&
    !hasFreeUserReachedScanLimit(totalScansUsed, maxTotalScans, isFreeTierOrg)

  // Whether a scan can be attempted. Wider than canScan: an org at its
  // free-tier cap still gets a live button, so the 402 can explain itself.
  const scanBlock = getScanBlock({
    canWrite,
    isPaused,
    isCancelled,
    isArchived: fundingNeed.is_archived,
    hasAmount: hasScanAmount(fundingNeed),
  })
  const canStartScan = !scanBlock

  const { data } = useLatestScanResultsByNeedId(
    token,
    Number(fundingNeed.id),
  ) as { data: ScanResponse[]; isLoading: boolean; isError: boolean }

  const { scanDate, latestScanFoundNothing } = getLatestScan(
    data && data.length > 0 ? data[0].scan_date : null,
    fundingNeed.last_scanned_at,
  )

  // Once a later scan found nothing, older results are not valid matches and
  // are not offered. The API already leaves them out; this also covers results
  // cached from before that scan.
  const hasResults =
    !latestScanFoundNothing &&
    Boolean(data?.length > 0 || (scanIds && scanIds.length > 0))
  const scanDaysOld =
    scanDate && isValid(scanDate) ? differenceInDays(new Date(), scanDate) : 0
  const isScanResultsStale =
    scanDaysOld >= STALE_SCAN_DAYS && !fundingNeed.is_archived

  const updatedAt = fundingNeed.updated_at
    ? parseUTCDate(fundingNeed.updated_at)
    : null
  const isEditedSinceLastScan =
    Boolean(
      updatedAt &&
        isValid(updatedAt) &&
        scanDate &&
        isValid(scanDate) &&
        updatedAt > scanDate,
    ) && !fundingNeed.is_archived

  const showStaleWarning = isEditedSinceLastScan || isScanResultsStale

  // View Results stays visible but disabled for stale results — clearer for
  // users to see it's there but blocked than to have it vanish. Stays
  // enabled as a fallback when the org can't currently re-scan (paused/at
  // cap), since it's the only way back to their existing matches.
  const viewResultsDisabled = showStaleWarning && canScan

  const warningMessage =
    hasResults && isEditedSinceLastScan
      ? EDITED_SINCE_SCAN_MESSAGE
      : hasResults && isScanResultsStale
        ? `Scan results are ${scanDaysOld} days old. Consider re-scanning for up-to-date matches.`
        : null

  return {
    data,
    hasResults,
    canScan,
    scanDaysOld,
    isScanResultsStale,
    isEditedSinceLastScan,
    showStaleWarning,
    viewResultsDisabled,
    warningMessage,
    latestScanFoundNothing,
    canStartScan,
    scanBlockedReason: scanBlock?.message ?? null,
    scanBlockedScope: scanBlock?.scope ?? null,
  }
}
